// Sync Status Component
// Hiển thị trạng thái đồng bộ với server

import { useState, useEffect } from 'react';
import { syncService } from '../../services/sync/SyncService';
import { Cloud, CloudOff, RefreshCw, Check, AlertCircle } from 'lucide-react';

export default function SyncStatus() {
  // ⚠️ WIDGET ẨN: Sync vẫn chạy tự động ở background, không hiển thị UI
  return null;

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueSize, setQueueSize] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<number>(0);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Initial update
    const updateStatus = () => {
      const online = syncService.isOnline();
      const queue = syncService.getQueueSize();
      const syncing = syncService.isSyncInProgress();
      const lastSync = syncService.getLastSyncTime();
      
      setIsOnline(online);
      setQueueSize(queue);
      setIsSyncing(syncing);
      setLastSyncTime(lastSync);
      
      // Debug log (chỉ khi có thay đổi)
      if (queue !== queueSize || syncing !== isSyncing) {
        console.log(`🔄 [SyncStatus] Queue: ${queue}, Syncing: ${syncing}, Online: ${online}`);
      }
    };
    
    // Update ngay lập tức
    updateStatus();
    
    // Update mỗi giây
    const interval = setInterval(updateStatus, 1000);

    return () => clearInterval(interval);
  }, [queueSize, isSyncing]);

  const handleManualSync = async () => {
    try {
      setIsSyncing(true);
      await syncService.syncQueue();
    } catch (error) {
      console.error('Manual sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (isSyncing) return 'Đang đồng bộ...';
    if (queueSize > 0) return `${queueSize} items cần đồng bộ`;
    return 'Đã đồng bộ';
  };

  const getStatusIcon = () => {
    if (!isOnline) return <CloudOff className="w-4 h-4 text-gray-500" />;
    if (isSyncing) return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
    if (queueSize > 0) return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    return <Check className="w-4 h-4 text-green-500" />;
  };

  const getStatusColor = () => {
    if (!isOnline) return 'bg-gray-100 text-gray-700';
    if (isSyncing) return 'bg-blue-50 text-blue-700';
    if (queueSize > 0) return 'bg-yellow-50 text-yellow-700';
    return 'bg-green-50 text-green-700';
  };

  const formatLastSyncTime = () => {
    if (lastSyncTime === 0) return 'Chưa đồng bộ';
    
    const diff = Date.now() - lastSyncTime;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h trước`;
    if (minutes > 0) return `${minutes}p trước`;
    return `${seconds}s trước`;
  };

  // ⚠️ OPTIMIZATION: Chỉ hiển thị khi có queue hoặc đang sync
  if (!showDetails && queueSize === 0 && !isSyncing && lastSyncTime > 0) {
    // Đã sync xong, ẩn widget để không làm phiền user
    return null;
  }

  return (
    <div className="fixed top-20 right-4 z-40">
      {/* Compact Status Button */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg transition-all ${getStatusColor()} hover:shadow-xl text-xs`}
      >
        {getStatusIcon()}
        <span className="font-medium">{getStatusText()}</span>
        {queueSize > 0 && (
          <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {queueSize}
          </span>
        )}
      </button>

      {/* Detailed Status Panel */}
      {showDetails && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border w-80 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4">
            <h3 className="text-white font-bold flex items-center gap-2">
              <Cloud className="w-5 h-5" />
              Trạng thái đồng bộ
            </h3>
          </div>

          {/* Content */}
          <div className="p-4 space-y-3">
            {/* Online Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Kết nối</span>
              <div className="flex items-center gap-2">
                {isOnline ? (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm font-semibold text-green-600">Online</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-gray-400 rounded-full" />
                    <span className="text-sm font-semibold text-gray-600">Offline</span>
                  </>
                )}
              </div>
            </div>

            {/* Queue Size */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Hàng đợi</span>
              <span className={`text-sm font-semibold ${queueSize > 0 ? 'text-yellow-600' : 'text-gray-600'}`}>
                {queueSize} items
              </span>
            </div>

            {/* Last Sync Time */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Đồng bộ lần cuối</span>
              <span className="text-sm font-semibold text-gray-600">
                {formatLastSyncTime()}
              </span>
            </div>

            {/* Sync Status */}
            {isSyncing && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
                <span className="text-sm text-blue-700">Đang đồng bộ dữ liệu...</span>
              </div>
            )}

            {/* Manual Sync Button */}
            <button
              onClick={handleManualSync}
              disabled={!isOnline || isSyncing || queueSize === 0}
              className="w-full py-2 px-4 bg-blue-500 text-white rounded-lg font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Đang đồng bộ...' : 'Đồng bộ ngay'}
            </button>

            {/* Info Messages */}
            {!isOnline && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  📴 Bạn đang offline. Dữ liệu sẽ được đồng bộ tự động khi kết nối lại.
                </p>
              </div>
            )}

            {isOnline && queueSize === 0 && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-green-700">
                  ✅ Tất cả dữ liệu đã được đồng bộ với server.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

